import type { PermissionCatalogEntry, UserAuthorizationSummary } from '@/types';

export const PERMISSIONS = {
  COMPANY_READ: 'company.read',
  COMPANY_UPDATE: 'company.update',
  COMPANY_ONBOARDING_FINISH: 'company.onboarding.finish',

  SERVICE_READ: 'service.read',
  SERVICE_CREATE: 'service.create',
  SERVICE_UPDATE: 'service.update',
  SERVICE_DELETE: 'service.delete',

  PRODUCT_READ: 'product.read',
  PRODUCT_CREATE: 'product.create',
  PRODUCT_UPDATE: 'product.update',
  PRODUCT_DELETE: 'product.delete',

  BOOKING_READ: 'booking.read',
  BOOKING_CREATE: 'booking.create',
  BOOKING_STATUS_UPDATE: 'booking.status.update',
  BOOKING_AVAILABILITY_READ: 'booking.availability.read',

  WALK_IN_QUEUE_READ: 'walk-in-queue.read',
  WALK_IN_QUEUE_CREATE: 'walk-in-queue.create',
  WALK_IN_QUEUE_STATUS_UPDATE: 'walk-in-queue.status.update',
  WALK_IN_QUEUE_ENTRY_CREATE: 'walk-in-queue.entry.create',
  WALK_IN_QUEUE_ENTRY_STATUS_UPDATE: 'walk-in-queue.entry.status.update',

  ATTENDANCE_CREATE: 'attendance.create',
  ATTENDANCE_UPDATE: 'attendance.update',
  ATTENDANCE_STATUS_UPDATE: 'attendance.status.update',

  USER_COMPANY_PROFESSIONAL_READ: 'user-company.professional.read',
  USER_COMPANY_PROFESSIONAL_CREATE: 'user-company.professional.create',
  USER_COMPANY_PROFESSIONAL_UPDATE: 'user-company.professional.update',
  USER_COMPANY_PROFESSIONAL_DELETE: 'user-company.professional.delete',
  USER_COMPANY_PROFESSIONAL_ALL: 'user-company.professional.*',

  USER_COMPANY_CUSTOMER_READ: 'user-company.customer.read',
  USER_COMPANY_CUSTOMER_CREATE: 'user-company.customer.create',
  USER_COMPANY_CUSTOMER_UPDATE: 'user-company.customer.update',

  COMPANY_WORKING_HOUR_READ: 'company-working-hour.read',
  COMPANY_WORKING_HOUR_CREATE: 'company-working-hour.create',
  COMPANY_WORKING_HOUR_UPDATE: 'company-working-hour.update',
  COMPANY_WORKING_HOUR_DELETE: 'company-working-hour.delete',

  USER_READ: 'user.read',

  DASHBOARD_READ: 'dashboard.read',
  DASHBOARD_ADMIN_READ: 'dashboard.admin.read',
  DASHBOARD_PROFESSIONAL_READ: 'dashboard.professional.read',

  NOTIFICATION_READ: 'notification.read',
  NOTIFICATION_UPDATE: 'notification.update',

  RBAC_MANAGE: 'rbac.manage',
} as const;

export type PermissionCode = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

function normalizeObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function getNormalizedCode(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const code = value.trim();
  return code.length > 0 ? code : null;
}

function toPermissionCodeSet(values: unknown[]): Set<string> {
  const codes = new Set<string>();
  for (const value of values) {
    const normalized = getNormalizedCode(value);
    if (normalized) codes.add(normalized);
  }
  return codes;
}

export function normalizePermissionCodes(source: unknown): string[] {
  if (!Array.isArray(source)) return [];
  return Array.from(toPermissionCodeSet(source));
}

export function extractPermissionCodes(raw: Record<string, unknown>): string[] {
  const fromPrimary = normalizePermissionCodes(raw.permissionCodes ?? raw.permissions ?? raw.effectivePermissions);
  if (fromPrimary.length > 0) return fromPrimary;

  const extraCandidates = [
    raw.effectivePermissionCodes,
    raw.rbacPermissions,
    raw.allowedPermissions,
    raw.grantedPermissions,
  ];

  for (const candidate of extraCandidates) {
    const normalized = normalizePermissionCodes(candidate);
    if (normalized.length > 0) return normalized;
  }

  return [];
}

function normalizePermissionCatalog(source: unknown): PermissionCatalogEntry[] {
  const rows = Array.isArray(source)
    ? source
    : (normalizeObject(source)?.permissionCatalog ??
      normalizeObject(source)?.catalog ??
      normalizeObject(source)?.permissions ??
      normalizeObject(source)?.data);
  if (!Array.isArray(rows)) return [];

  const catalog = new Map<string, PermissionCatalogEntry>();

  for (const row of rows) {
    const record = normalizeObject(row);
    if (!record) continue;
    const permissionRecord = normalizeObject(record.permission);

    const code =
      getNormalizedCode(record.code) ??
      getNormalizedCode(record.permissionCode) ??
      getNormalizedCode(permissionRecord?.code);
    if (!code) continue;

    const resource = getNormalizedCode(record.resource) ?? getNormalizedCode(permissionRecord?.resource) ?? undefined;
    const action = getNormalizedCode(record.action) ?? getNormalizedCode(permissionRecord?.action) ?? undefined;
    const description =
      getNormalizedCode(record.description) ?? getNormalizedCode(permissionRecord?.description) ?? undefined;

    catalog.set(code, {
      code,
      ...(resource ? { resource } : {}),
      ...(action ? { action } : {}),
      ...(description ? { description } : {}),
    });
  }

  return Array.from(catalog.values());
}

export function extractAuthorizationSummary(payload: unknown): UserAuthorizationSummary | null {
  const root = normalizeObject(payload);
  if (!root) return null;

  const nested = normalizeObject(root.data);
  const source = nested ?? root;
  const permissionCodes = extractPermissionCodes(source);
  const permissionCatalog = normalizePermissionCatalog(
    source.permissionCatalog ?? source.catalog ?? source.permissions ?? source.data,
  );
  const catalogVersion =
    typeof source.catalogVersion === 'number'
      ? source.catalogVersion
      : typeof root.catalogVersion === 'number'
        ? root.catalogVersion
        : undefined;
  const userId = getNormalizedCode(source.userId) ?? getNormalizedCode(root.userId) ?? undefined;
  const companyId = getNormalizedCode(source.companyId) ?? getNormalizedCode(root.companyId) ?? undefined;

  if (
    permissionCodes.length === 0 &&
    permissionCatalog.length === 0 &&
    catalogVersion === undefined &&
    !userId &&
    !companyId
  ) {
    return null;
  }

  return {
    ...(userId ? { userId } : {}),
    ...(companyId ? { companyId } : {}),
    ...(catalogVersion !== undefined ? { catalogVersion } : {}),
    permissionCodes,
    permissionCatalog,
  };
}

export function hasPermissionCode(userPermissionCodes: Iterable<string>, requiredCode: string): boolean {
  const required = requiredCode.trim();
  if (!required) return true;

  const permissionSet = userPermissionCodes instanceof Set ? userPermissionCodes : new Set(userPermissionCodes);

  if (permissionSet.has('*') || permissionSet.has(required)) {
    return true;
  }

  const segments = required.split('.');
  for (let i = segments.length - 1; i > 0; i -= 1) {
    const wildcardCode = `${segments.slice(0, i).join('.')}.*`;
    if (permissionSet.has(wildcardCode)) {
      return true;
    }
  }

  return false;
}

export function resolveEffectivePermissionCodes({
  explicitPermissionCodes,
  isSuperAdmin,
}: {
  explicitPermissionCodes: string[];
  isSuperAdmin: boolean;
}): string[] {
  if (isSuperAdmin) {
    return ['*'];
  }

  const normalizedExplicitCodes = Array.from(toPermissionCodeSet(explicitPermissionCodes));
  if (normalizedExplicitCodes.length > 0) {
    return normalizedExplicitCodes;
  }

  return [];
}
